// Frontend Types - Replacing ../types imports

export interface User {
  id: string
  email: string
  firstName?: string
  lastName?: string
  role: UserRole
  verified: boolean
  isEnterprise: boolean
  companyName?: string
  phone?: string
  street?: string
  city?: string
  state?: string
  zipCode?: string
  country?: string
  locationCity?: string
  locationState?: string
  avatar?: string
  businessLicense?: string
  certifications?: string[]
  specialties?: string[]
  isEmailVerified: boolean
  emailSent?: boolean
  about?: string
  cageCode?: string
  dunsNumber?: string
  gsaScheduleId?: string
  govCertifications?: string[]
  approvedAgencies?: string[]
  govDocsUrls?: string[]
  govStatus?: string
  isAgency?: boolean
  isGovCertified?: boolean
  agencyId?: string
  banned?: boolean
  termsAcceptedAt?: string
  termsVersion?: string
  privacyAcceptedAt?: string
  privacyVersion?: string
  address?: {
    street?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
  }
  createdAt: string
  updatedAt: string
}

export interface Product {
  id: string
  title: string
  description: string
  price: number
  imageUrls: string[]
  categoryId: string
  ownerId: string
  condition: ProductCondition
  status: ProductStatus
  location?: string
  specifications?: ProductSpecification[]
  createdAt: string
  updatedAt: string
  category?: Category
  owner?: User
  bids?: Bid[]
  currentBid?: number
  auctionEndTime?: string
  auctionEndDate?: string
  isAuction?: boolean
  startingBid?: number
  reservePrice?: number
  minimumBid?: number
  buyNowPrice?: number
  bidCount?: number
}

export interface Category {
  id: string
  name: string
  description?: string
  imageUrl?: string
  parentId?: string
  createdAt: string
  updatedAt: string
  parent?: Category
  children?: Category[]
  productCount?: number
}

export interface Bid {
  id: string
  amount: number
  productId: string
  userId: string
  createdAt: string
  updatedAt: string
  product?: Product
  user?: User
  bidder?: User
  isWinning?: boolean
}

export interface Order {
  id: string
  productId: string
  buyerId: string
  sellerId: string
  quantity: number
  totalAmount: string
  status: OrderStatus
  paymentStatus: PaymentStatus
  shippingAddress?: string
  shippingStreet?: string
  shippingCity?: string
  shippingState?: string
  shippingZipCode?: string
  shippingCountry?: string
  trackingNumber?: string
  notes?: string
  stripeFee?: string
  paymentType?: string
  paymentLast4?: string
  paymentBrand?: string
  createdAt: string
  updatedAt: string
  product?: Product
  buyer?: User
  seller?: User
  payment?: Payment
}

export interface Payment {
  id: string
  orderId: string
  userId: string
  amount: string
  currency: string
  status: PaymentStatus
  paymentMethod: PaymentType
  stripePaymentIntentId?: string
  createdAt: string
  updatedAt: string
  order?: Order
  user?: User
}

export interface Message {
  id: string
  conversationId: string
  senderId: string
  receiverId: string
  content: string
  messageType: MessageType
  isRead: boolean
  createdAt: string
  updatedAt: string
  sentAt?: string
  sender?: User
  receiver?: User
  conversation?: Conversation
}

export interface Conversation {
  id: string
  productId?: string
  createdAt: string
  updatedAt: string
  participants?: ConversationParticipant[]
  messages?: Message[]
  product?: Product
}

export interface ConversationParticipant {
  id: string
  conversationId: string
  userId: string
  createdAt: string
  conversation?: Conversation
  user?: User
}

export interface ProductSpecification {
  id: string
  productId: string
  key: string
  name: string
  value: string
  unit?: string
  createdAt: string
  updatedAt: string
}

export interface ApiResponse<T = any> {
  success: boolean
  message?: string
  data?: T
  error?: string
  errors?: Record<string, string[]>
}

export interface PaginatedResponse<T = any> {
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

export interface ProductSearchParams {
  q?: string
  category?: string
  condition?: ProductCondition
  minPrice?: number
  maxPrice?: number
  location?: string
  isAuction?: boolean
  sortBy?: ProductSortBy
  sortOrder?: SortOrder
  page?: number
  limit?: number
}

// Enums
export enum UserRole {
  ADMIN = 'ADMIN',
  REGULAR_BUYER = 'REGULAR_BUYER',
  REGULAR_VENDOR = 'REGULAR_VENDOR',
  ENTERPRISE_BUYER = 'ENTERPRISE_BUYER',
  ENTERPRISE_VENDOR = 'ENTERPRISE_VENDOR',
  AGENCY = 'AGENCY',
  BUYER = 'BUYER',
  VENDOR = 'VENDOR',
  MODERATOR = 'MODERATOR'
}

export enum ProductCondition {
  NEW = 'NEW',
  LIKE_NEW = 'LIKE_NEW',
  EXCELLENT = 'EXCELLENT',
  GOOD = 'GOOD',
  FAIR = 'FAIR',
  POOR = 'POOR'
}

export enum ProductStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SOLD = 'SOLD',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
  DRAFT = 'DRAFT',
  EXPIRED = 'EXPIRED',
  SUSPENDED = 'SUSPENDED'
}

export enum OrderStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  SHIPPED = 'SHIPPED',
  DELIVERED = 'DELIVERED',
  CANCELLED = 'CANCELLED',
  REFUNDED = 'REFUNDED'
}

export enum PaymentStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
  REFUNDED = 'REFUNDED'
}

export enum PaymentType {
  CREDIT_CARD = 'CREDIT_CARD',
  DEBIT_CARD = 'DEBIT_CARD',
  BANK_TRANSFER = 'BANK_TRANSFER',
  DIGITAL_WALLET = 'DIGITAL_WALLET',
  CRYPTO = 'CRYPTO'
}

export enum MessageType {
  TEXT = 'TEXT',
  IMAGE = 'IMAGE',
  FILE = 'FILE',
  SYSTEM = 'SYSTEM'
}

export enum ProductSortBy {
  PRICE = 'PRICE',
  CREATED_AT = 'CREATED_AT',
  TITLE = 'TITLE',
  POPULARITY = 'POPULARITY',
  ENDING_SOON = 'ENDING_SOON'
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC'
}

export enum ConversationStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
  BLOCKED = 'BLOCKED'
}

export enum ConversationType {
  DIRECT = 'DIRECT',
  PRODUCT = 'PRODUCT'
}

export interface GovAgency {
  id: string
  name: string
  email: string
  contactPerson: string
  approved: boolean
  createdAt: string
  updatedAt: string
}

export interface PurchaseOrder {
  id: string
  poNumber: string
  buyerId: string
  vendorId: string
  status: string
  totalAmount: number
  createdAt: string
  updatedAt: string
  buyer?: User
  vendor?: User
}

export interface Invoice {
  id: string
  poId: string
  invoiceNumber: string
  amount: number
  tax: number
  total: number
  dueDate: string
  status: string
  notes?: string
  paymentProofUrl?: string
  paymentProofUploadedAt?: string
  paymentProofUploadedBy?: string
  // Enhanced payment tracking fields
  paymentDate?: string
  paymentMethod?: string
  paymentReference?: string
  paymentConfirmedBy?: string
  paymentConfirmedAt?: string
  daysToPay?: number
  createdAt: string
  updatedAt: string
  po?: PurchaseOrder
} 